import { useState, useEffect, useMemo, lazy, Suspense, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  MdWork, MdSearch, MdFilterList, MdLocationOn, MdStar, MdAdd, MdMap, MdPerson, 
  MdCheckCircle, MdSchedule, MdFavorite, MdVerified, MdClose, MdExpandMore, 
  MdExpandLess, MdSort, MdClear, MdMessage
} from 'react-icons/md';
import api from '../../services/api';
import { getUser } from '../../services/auth';
import { t } from '../../utils/i18n';
import { getCityCoordinates } from '../../utils/geoUtils';
import LocationAutocomplete from '../../components/LocationAutocomplete';
import PaywallModal from '../../components/PaywallModal';
import ResultsToolbar from '../../components/search/ResultsToolbar';
import ResultsList from '../../components/search/ResultsList';
import MapPanel from '../../components/search/MapPanel';

// Lazy load MapView to avoid SSR issues with react-leaflet
const MapView = lazy(() => import('../../components/MapView'));

// Generate consistent color based on name
const getAvatarColor = (name) => {
  if (!name) return '9333ea';
  const colors = ['9333ea', '3b82f6', '10b981', 'f59e0b', 'ef4444', 'ec4899', '8b5cf6', '06b6d4', 'f97316', '14b8a6', '6366f1', '84cc16'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Haversine formula for calculating distance between two coordinates (in km)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Skeleton Card Component
const SkeletonCard = () => (
  <div className="card animate-pulse">
    <div className="flex items-start gap-4">
      <div className="w-16 h-16 rounded-[18px] bg-gray-200 dark:bg-gray-700"></div>
      <div className="flex-1 space-y-3">
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
        <div className="flex gap-2">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-20"></div>
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-16"></div>
        </div>
      </div>
      <div className="text-right space-y-2">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
      </div>
    </div>
  </div>
);

// Extract a real "city" from LocationAutocomplete labels like:
// - "Zvezdara, Beograd, Srbija" -> "Beograd"
// - "Beograd (Zvezdara), Srbija" -> "Beograd"
// - "Novi Sad, Srbija" -> "Novi Sad"
// Never return "Srbija/Serbia" as city.
function extractCityFromLocationLabel(label) {
  if (!label) return undefined;
  const s = String(label).trim();
  if (!s) return undefined;

  // Strong Belgrade heuristics (latin + cyrillic + common municipality terms)
  if (/belgrade|beograd|београд|stari\s*grad|стари\s*град|градска\s*општина|општина/i.test(s)) return 'Beograd';

  // Split by commas and drop country tokens
  const parts = s.split(',').map((p) => p.trim()).filter(Boolean);
  const countryRe = /^(srbija|serbia|republika\s+srbija|republic\s+of\s+serbia)$/i;
  const filtered = parts.filter((p) => !countryRe.test(p));
  const candidate = (filtered.length ? filtered[filtered.length - 1] : parts[0]) || '';

  // Normalize common Serbian cities
  if (/novi\s*sad|нови\s*сад/i.test(candidate)) return 'Novi Sad';
  if (/niš|nis|ниш/i.test(candidate)) return 'Niš';

  // If candidate still contains Belgrade hints in parentheses, normalize to Beograd
  if (/belgrade|beograd|београд/i.test(candidate)) return 'Beograd';

  // Avoid returning country names accidentally
  if (countryRe.test(candidate)) return undefined;
  return candidate;
}

export default function JobsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = getUser();
  
  // State
  const [babysitters, setBabysitters] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMap, setShowMap] = useState(true); // Show map by default on desktop
  const [mapViewMode, setMapViewMode] = useState('list'); // 'list' or 'map' for mobile
  const [friends, setFriends] = useState([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showSearchHeader, setShowSearchHeader] = useState(false);
  const [sortBy, setSortBy] = useState('recommended');
  const [selectedLocation, setSelectedLocation] = useState(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const lat = params.get('lat');
    const lon = params.get('lon');
    const search = params.get('search') || params.get('location');
    if (lat != null && lon != null && search) {
      const city = extractCityFromLocationLabel(search);
      return {
        lat: parseFloat(lat),
        lon: parseFloat(lon),
        label: search,
        city,
      };
    }
    return null;
  });
  const [mapBounds, setMapBounds] = useState(null);
  const [showSearchAreaButton, setShowSearchAreaButton] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [highlightedItem, setHighlightedItem] = useState(null);
  const [selectedPinItemIds, setSelectedPinItemIds] = useState(null); // null = svi; array = filtrirano po pin-u
  const mapRef = useRef(null);
  // Always ON: list follows current map bounds (no toggle)
  const [searchAsIMove] = useState(true);
  const [appliedListBounds, setAppliedListBounds] = useState(null);
  const [listPage, setListPage] = useState(1);
  const PAGE_SIZE = 24;
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  
  // Get initial values from URL params
  const locationQuery = searchParams.get('search') || searchParams.get('location') || '';
  const modeParam = searchParams.get('mode');
  const isParent = user?.role === 'parent';
  const initialMode = modeParam === 'requests' ? 'requests' : modeParam === 'offers' ? 'offers' : (isParent ? 'offers' : 'requests');
  
  const [viewMode, setViewMode] = useState(initialMode);
  const isBabysitterOffers = viewMode === 'offers';

  const [searchQuery, setSearchQuery] = useState(locationQuery);
  const [locationInput, setLocationInput] = useState(locationQuery);
  
  // Filters state
  const [filters, setFilters] = useState({
    city: searchParams.get('city') || 'all',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    minRating: searchParams.get('minRating') || '',
    experience: searchParams.get('experience') || 'all',
    availability: searchParams.get('availability')?.split(',') || [],
    verified: searchParams.get('verified') === 'true',
    nonSmoker: searchParams.get('nonSmoker') === 'true',
    multipleChildren: searchParams.get('multipleChildren') === 'true',
    distance: searchParams.get('distance') || 'all',
  });

  // Debounce filter updates
  const [filterDebounceTimer, setFilterDebounceTimer] = useState(null);

  // Sync selectedLocation from URL when it changes (e.g. user navigates with new search)
  const urlLat = searchParams.get('lat');
  const urlLon = searchParams.get('lon');
  const urlSearch = searchParams.get('search') || searchParams.get('location');
  useEffect(() => {
    if (urlLat != null && urlLon != null && urlSearch) {
      const city = extractCityFromLocationLabel(urlSearch);
      const loc = {
        lat: parseFloat(urlLat),
        lon: parseFloat(urlLon),
        label: urlSearch,
        city,
      };
      setSelectedLocation(loc);
      setSearchQuery(urlSearch);
      setLocationInput(urlSearch);
    }
  }, [urlLat, urlLon, urlSearch]);

  useEffect(() => {
    loadData();
    loadFriends();
  }, [viewMode]);

  useEffect(() => {
    loadData();
  }, [selectedLocation?.lat, selectedLocation?.lon]);

  useEffect(() => {
    // reset paging and applied bounds when mode or results change
    setListPage(1);
    setAppliedListBounds(null);
    setSelectedPinItemIds(null);
  }, [viewMode, filteredResults.length]);

  useEffect(() => {
    applyFilters();
  }, [babysitters, jobs, searchQuery, filters, viewMode, sortBy]);

  const loadFriends = async () => {
    try {
      const response = await api.get('/friends').catch(() => ({ data: [] }));
      setFriends(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading friends:', error);
      setFriends([]);
    }
  };

  // Normalizuj grad za API (kao na Landing) — search koristi ime, ne adresu
  const normalizeCityForApi = useCallback((cityOrLabel) => {
    if (!cityOrLabel) return undefined;
    const s = String(cityOrLabel).toLowerCase();
    const isBelgrade = /belgrade|beograd|београд|stari\s*grad|стари\s*град|градска\s*општина|општина\s*стари/i.test(s) || s.includes('beograd') || s.includes('београд');
    if (isBelgrade) return 'Beograd';
    if (/novi\s*sad|нови\s*сад/i.test(s)) return 'Novi Sad';
    if (/niš|nis|ниш/i.test(s)) return 'Niš';
    return cityOrLabel === 'Belgrade' ? 'Beograd' : cityOrLabel;
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (viewMode === 'offers') {
        const label = selectedLocation?.label || searchQuery || '';
        const cityFromLabel = selectedLocation?.city || label.split(',')[0]?.trim() || searchQuery?.split(',')[0]?.trim();
        const cityForApi = /beograd|belgrade|београд/i.test(label) ? 'Beograd' : cityFromLabel;
        const cityParam = filters.city !== 'all' ? filters.city : (normalizeCityForApi(cityForApi) || undefined);
        const response = await api.get('/users/providers', {
          params: {
            limit: 500,
            city: cityParam,
            minPrice: filters.minPrice || undefined,
            maxPrice: filters.maxPrice || undefined
          }
        });
        setBabysitters(response.data?.providers || []);
      } else {
        // Get all posted jobs for browsing
        const response = await api.get('/jobs', {
          params: {
            status: 'posted',
            limit: 200
          }
        }).catch(() => ({ data: { jobs: [] } }));
        const allJobs = response.data?.jobs || (Array.isArray(response.data) ? response.data : []);
        // Filter to only posted jobs
        setJobs(allJobs.filter(job => job.status === 'posted'));
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Došlo je do greške pri učitavanju rezultata');
      if (viewMode === 'offers') {
        setBabysitters(getMockBabysitters());
      } else {
        setJobs(getMockJobs());
      }
    } finally {
      setLoading(false);
      setMapBounds(null); // nova pretraga — prikaži sve dok mapa ne pošalje bounds (kao na Landing)
    }
  };

  // Mock data functions (same as before)
  const getMockBabysitters = () => {
    const userAddress = user?.address;
    const userCoords = userAddress?.lat && userAddress?.lng 
      ? { lat: parseFloat(userAddress.lat), lng: parseFloat(userAddress.lng) }
      : { lat: 44.8008, lng: 20.5085 };
    
    const generateNearbyListing = (index, baseCoords) => {
      const angle = (index * 60) * (Math.PI / 180);
      const radius = 0.01 + (index * 0.002);
      return {
        lat: baseCoords.lat + radius * Math.cos(angle),
        lng: baseCoords.lng + radius * Math.sin(angle)
      };
    };
    
    return [
      {
        id: 1, firstName: 'Isidora', lastName: 'Marković', email: 'isidora@example.com',
        photoUrl: null, rating: { average: 5, count: 2 }, hourlyRate: 800,
        address: { city: 'Beograd', street: 'Stanislava Sremčevića 15', ...generateNearbyListing(0, userCoords) },
        isVerified: true, age: 24, experience: 3, responseTime: 60, isSupersitter: true, favorites: 6
      },
      {
        id: 2, firstName: 'Dušanka', lastName: 'Petrović', email: 'dusanka@example.com',
        photoUrl: null, rating: { average: 4.5, count: 5 }, hourlyRate: 550,
        address: { city: 'Beograd', street: 'Vojvode Stepe Stepanovića 42', ...generateNearbyListing(1, userCoords) },
        isVerified: true, age: 32, experience: 8, references: 1, favorites: 1
      },
      {
        id: 3, firstName: 'Jelena', lastName: 'Jovanović', email: 'jelena@example.com',
        photoUrl: null, rating: { average: 5, count: 3 }, hourlyRate: 700,
        address: { city: 'Beograd', street: 'Vračar', ...generateNearbyListing(2, userCoords) },
        isVerified: true, age: 28, experience: 10, responseTime: 49, isSupersitter: true, favorites: 1
      },
      {
        id: 4, firstName: 'Selena', lastName: 'Nikolić', email: 'selena@example.com',
        photoUrl: null, rating: { average: 4.8, count: 4 }, hourlyRate: 800,
        address: { city: 'Beograd', street: 'Zvezdarska 28', ...generateNearbyListing(3, userCoords) },
        isVerified: true, age: 21, experience: 2, responseTime: 28, isSupersitter: true, favorites: 6
      },
      {
        id: 5, firstName: 'Meri', lastName: 'Stojanović', email: 'meri@example.com',
        photoUrl: null, rating: { average: 4.7, count: 6 }, hourlyRate: 1000,
        address: { city: 'Beograd', street: 'Knez Mihailova 15', lat: 44.8178, lng: 20.4569 },
        isVerified: true, age: 29, experience: 8, hasCertificate: true, favorites: 2
      },
      {
        id: 6, firstName: 'Sanja', lastName: 'Milošević', email: 'sanja@example.com',
        photoUrl: null, rating: { average: 5, count: 8 }, hourlyRate: 800,
        address: { city: 'Beograd', street: 'Novi Beograd, Blok 45', lat: 44.8028, lng: 20.4256 },
        isVerified: true, age: 26, experience: 8, responseTime: 5, isSupersitter: true, favorites: 4
      },
    ];
  };

  const getMockJobs = () => {
    const userAddress = user?.address;
    const userCoords = userAddress?.lat && userAddress?.lng 
      ? { lat: parseFloat(userAddress.lat), lng: parseFloat(userAddress.lng) }
      : { lat: 44.8008, lng: 20.5085 };
    
    return [
      {
        id: 1, title: 'Tražim bebisiterku za vikend',
        description: 'Tražim pouzdanu bebisiterku za vikend, subota i nedelja, 10h dnevno.',
        status: 'posted', scheduledDate: '2025-12-20', startTime: '08:00', endTime: '18:00',
        hourlyRate: 800, location: { city: 'Beograd', street: 'Vračar', lat: userCoords.lat + 0.01, lng: userCoords.lng + 0.01 },
        parentId: 1
      },
      {
        id: 2, title: 'Bebisiterka za dnevnu negu',
        description: 'Tražim iskusnu bebisiterku za dnevnu negu deteta, radno vreme 9-17h.',
        status: 'posted', scheduledDate: '2025-12-15', startTime: '09:00', endTime: '17:00',
        hourlyRate: 750, location: { city: 'Beograd', street: 'Zvezdara', lat: 44.8000, lng: 20.5100 },
        parentId: 2
      },
    ];
  };

  const updateFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    
    // Update URL params
    const params = new URLSearchParams();
    if (newFilters.city !== 'all') params.set('city', newFilters.city);
    if (newFilters.minPrice) params.set('minPrice', newFilters.minPrice);
    if (newFilters.maxPrice) params.set('maxPrice', newFilters.maxPrice);
    if (newFilters.minRating) params.set('minRating', newFilters.minRating);
    if (newFilters.experience !== 'all') params.set('experience', newFilters.experience);
    if (newFilters.availability.length > 0) params.set('availability', newFilters.availability.join(','));
    if (newFilters.verified) params.set('verified', 'true');
    if (newFilters.nonSmoker) params.set('nonSmoker', 'true');
    if (newFilters.multipleChildren) params.set('multipleChildren', 'true');
    if (newFilters.distance !== 'all') params.set('distance', newFilters.distance);
    if (searchQuery) params.set('search', searchQuery);
    if (viewMode) params.set('mode', viewMode);
    
    setSearchParams(params, { replace: true });
  }, [searchQuery, viewMode, setSearchParams]);

  const applyFilters = () => {
    let filtered = viewMode === 'offers' ? [...babysitters] : [...jobs];

    // Search query filter — kada je lokacija (npr. "Beograd, Opština Stari Grad"), filtriraj po gradu, ne po celom stringu
    if (searchQuery) {
      const isLocationLike = searchQuery.includes(',') || searchQuery.length > 30;
      const query = searchQuery.toLowerCase();
      const rawCity = searchQuery.split(',')[0]?.trim() || searchQuery;
      const cityForFilter = isLocationLike
        ? (/beograd|belgrade|београд/i.test(searchQuery) ? 'Beograd' : normalizeCityForApi(rawCity))
        : null;
      if (viewMode === 'offers') {
        if (isLocationLike && cityForFilter) {
          filtered = filtered.filter(b => (b.address?.city || '').toLowerCase().includes((cityForFilter || '').toLowerCase()));
        } else {
          filtered = filtered.filter(babysitter =>
            `${babysitter.firstName} ${babysitter.lastName}`.toLowerCase().includes(query) ||
            babysitter.email?.toLowerCase().includes(query) ||
            babysitter.address?.city?.toLowerCase().includes(query)
          );
        }
      } else {
        filtered = filtered.filter(job =>
          job.title?.toLowerCase().includes(query) ||
          job.description?.toLowerCase().includes(query) ||
          job.location?.city?.toLowerCase().includes(query)
        );
      }
    }

    // Apply filters
    if (viewMode === 'offers') {
      if (filters.city !== 'all') {
        filtered = filtered.filter(b => b.address?.city === filters.city);
      }
      if (filters.minPrice) {
        filtered = filtered.filter(b => (b.hourlyRate || 0) >= parseFloat(filters.minPrice));
      }
      if (filters.maxPrice) {
        filtered = filtered.filter(b => (b.hourlyRate || 0) <= parseFloat(filters.maxPrice));
      }
      if (filters.minRating) {
        filtered = filtered.filter(b => (b.rating?.average || 0) >= parseFloat(filters.minRating));
      }
      if (filters.experience !== 'all') {
        const expMap = { '0-1': [0, 1], '2-5': [2, 5], '5+': [5, 100] };
        const [min, max] = expMap[filters.experience] || [0, 100];
        filtered = filtered.filter(b => {
          const exp = b.experience || 0;
          return exp >= min && exp <= max;
        });
      }
      if (filters.verified) {
        filtered = filtered.filter(b => b.isVerified);
      }
      
      // Distance filter (client-side fallback)
      if (selectedLocation && filters.distance !== 'all') {
        const distanceKm = parseInt(filters.distance) || 20;
        filtered = filtered.filter(b => {
          if (!b.address?.lat || !b.address?.lng) return true; // Include items without coordinates
          const distance = calculateDistance(
            selectedLocation.lat,
            selectedLocation.lon,
            parseFloat(b.address.lat),
            parseFloat(b.address.lng)
          );
          return distance <= distanceKm;
        });
      }
    } else {
      if (filters.city !== 'all') {
        filtered = filtered.filter(j => j.location?.city === filters.city);
      }
      if (filters.minPrice) {
        filtered = filtered.filter(j => (j.hourlyRate || 0) >= parseFloat(filters.minPrice));
      }
      if (filters.maxPrice) {
        filtered = filtered.filter(j => (j.hourlyRate || 0) <= parseFloat(filters.maxPrice));
      }
      
      // Distance filter for jobs
      if (selectedLocation && filters.distance !== 'all') {
        const distanceKm = parseInt(filters.distance) || 20;
        filtered = filtered.filter(j => {
          if (!j.location?.lat || !j.location?.lng) return true;
          const distance = calculateDistance(
            selectedLocation.lat,
            selectedLocation.lon,
            parseFloat(j.location.lat),
            parseFloat(j.location.lng)
          );
          return distance <= distanceKm;
        });
      }
    }

    // Sort
    if (sortBy === 'rating') {
      filtered.sort((a, b) => (b.rating?.average || 0) - (a.rating?.average || 0));
    } else if (sortBy === 'price-low') {
      filtered.sort((a, b) => (a.hourlyRate || 0) - (b.hourlyRate || 0));
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => (b.hourlyRate || 0) - (a.hourlyRate || 0));
    }

    setFilteredResults(filtered);
  };

  const cities = useMemo(() => {
    if (viewMode === 'offers') {
      return [...new Set(babysitters.map(b => b.address?.city).filter(Boolean))].sort();
    }
    return [...new Set(jobs.map(j => j.location?.city).filter(Boolean))].sort();
  }, [babysitters, jobs, viewMode]);

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.city !== 'all') count++;
    if (filters.minPrice) count++;
    if (filters.maxPrice) count++;
    if (filters.minRating) count++;
    if (filters.experience !== 'all') count++;
    if (filters.availability.length > 0) count++;
    if (filters.verified) count++;
    if (filters.nonSmoker) count++;
    if (filters.multipleChildren) count++;
    if (filters.distance !== 'all') count++;
    return count;
  };

  const clearAllFilters = () => {
    const defaultFilters = {
      city: 'all',
      minPrice: '',
      maxPrice: '',
      minRating: '',
      experience: 'all',
      availability: [],
      verified: false,
      nonSmoker: false,
      multipleChildren: false,
      distance: 'all',
    };
    updateFilters(defaultFilters);
  };

  const removeFilter = (filterKey, value = null) => {
    const newFilters = { ...filters };
    if (filterKey === 'availability' && value) {
      newFilters.availability = newFilters.availability.filter(a => a !== value);
    } else {
      newFilters[filterKey] = value !== null ? value : (filterKey === 'city' ? 'all' : filterKey === 'experience' ? 'all' : filterKey === 'distance' ? 'all' : '');
    }
    updateFilters(newFilters);
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setLocationInput(location.label);
    setSearchQuery(location.label);
    
    // Update URL params
    const params = new URLSearchParams(searchParams);
    params.set('search', location.label);
    params.set('lat', location.lat.toString());
    params.set('lon', location.lon.toString());
    setSearchParams(params, { replace: true });
    
    // Reload data with new location
    loadData();
  };
  
  const handleSearch = (e) => {
    e?.preventDefault();
    setSearchQuery(locationInput);
    const params = new URLSearchParams(searchParams);
    if (locationInput) {
      params.set('search', locationInput);
    } else {
      params.delete('search');
      params.delete('lat');
      params.delete('lon');
    }
    setSearchParams(params, { replace: true });
    loadData();
  };
  
  const handleSearchArea = () => {
    if (!mapBounds) return;
    
    // Extract bounds from map
    const { north, south, east, west } = mapBounds;
    const centerLat = (north + south) / 2;
    const centerLon = (east + west) / 2;
    
    // Update location and search
    setSelectedLocation({ lat: centerLat, lon: centerLon, label: 'Izabrana oblast' });
    setSearchQuery('Izabrana oblast');
    setShowSearchAreaButton(false);
    
    // Reload data
    loadData();
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    return (
      <div className="flex items-center gap-0.5">
        {[...Array(5)].map((_, i) => (
          <MdStar
            key={i}
            className={`text-sm ${
              i < fullStars
                ? 'text-yellow-400 fill-yellow-400'
                : i === fullStars && hasHalfStar
                ? 'text-yellow-400 fill-yellow-400 opacity-50'
                : 'text-gray-300 dark:text-gray-600'
            }`}
          />
        ))}
      </div>
    );
  };

  // Filter Panel Component
  const FilterPanel = ({ isMobile = false }) => {
    const activeCount = getActiveFiltersCount();
    
    if (isMobile) {
      return (
        <div className={`w-full max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-t-[22px] rounded-b-none`}>
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Filteri</h3>
            {activeCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
              >
                Resetuj sve
              </button>
            )}
            <button
              onClick={() => setShowMobileFilters(false)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <MdClose className="text-xl" />
            </button>
          </div>
          <div className="p-4">

          {viewMode === 'offers' ? (
            <>
              {/* Price Range */}
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-medium mb-3 text-gray-900 dark:text-gray-100">
                  Cena po satu (RSD)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="number"
                    value={filters.minPrice}
                    onChange={(e) => updateFilters({...filters, minPrice: e.target.value})}
                    placeholder="Min"
                    className="input-base text-sm"
                  />
                  <input
                    type="number"
                    value={filters.maxPrice}
                    onChange={(e) => updateFilters({...filters, maxPrice: e.target.value})}
                    placeholder="Max"
                    className="input-base text-sm"
                  />
                </div>
              </div>

              {/* Experience */}
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-medium mb-3 text-gray-900 dark:text-gray-100">
                  Iskustvo
                </label>
                <div className="space-y-2">
                  {['0-1', '2-5', '5+'].map(exp => (
                    <label key={exp} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="experience"
                        checked={filters.experience === exp}
                        onChange={() => updateFilters({...filters, experience: exp})}
                        className="w-4 h-4 text-primary-600"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {exp === '0-1' ? '0-1 godina' : exp === '2-5' ? '2-5 godina' : '5+ godina'}
                      </span>
                    </label>
                  ))}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="experience"
                      checked={filters.experience === 'all'}
                      onChange={() => updateFilters({...filters, experience: 'all'})}
                      className="w-4 h-4 text-primary-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Sve</span>
                  </label>
                </div>
              </div>

              {/* Availability */}
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-medium mb-3 text-gray-900 dark:text-gray-100">
                  Dostupnost
                </label>
                <div className="space-y-2">
                  {['weekdays', 'weekend', 'evenings'].map(avail => (
                    <label key={avail} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.availability.includes(avail)}
                        onChange={(e) => {
                          const newAvail = e.target.checked
                            ? [...filters.availability, avail]
                            : filters.availability.filter(a => a !== avail);
                          updateFilters({...filters, availability: newAvail});
                        }}
                        className="w-4 h-4 text-primary-600 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {avail === 'weekdays' ? 'Radni dani' : avail === 'weekend' ? 'Vikend' : 'Večeri'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Rating */}
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-medium mb-3 text-gray-900 dark:text-gray-100">
                  Ocena
                </label>
                <div className="space-y-2">
                  {['4', '3'].map(rating => (
                    <label key={rating} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="rating"
                        checked={filters.minRating === rating}
                        onChange={() => updateFilters({...filters, minRating: rating})}
                        className="w-4 h-4 text-primary-600"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {rating}+ ⭐
                      </span>
                    </label>
                  ))}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="rating"
                      checked={!filters.minRating}
                      onChange={() => updateFilters({...filters, minRating: ''})}
                      className="w-4 h-4 text-primary-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Sve</span>
                  </label>
                </div>
              </div>

              {/* City */}
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-medium mb-3 text-gray-900 dark:text-gray-100">
                  Grad
                </label>
                <select
                  value={filters.city}
                  onChange={(e) => updateFilters({...filters, city: e.target.value})}
                  className="input-base text-sm"
                >
                  <option value="all">Svi gradovi</option>
                  {cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              {/* Verified */}
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.verified}
                    onChange={(e) => updateFilters({...filters, verified: e.target.checked})}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Verifikovan profil
                  </span>
                </label>
              </div>

              {/* Non-smoker */}
              <div className="mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.nonSmoker}
                    onChange={(e) => updateFilters({...filters, nonSmoker: e.target.checked})}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Ne puši
                  </span>
                </label>
              </div>
            </>
          ) : (
            <>
              {/* Job filters */}
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-medium mb-3 text-gray-900 dark:text-gray-100">
                  Grad
                </label>
                <select
                  value={filters.city}
                  onChange={(e) => updateFilters({...filters, city: e.target.value})}
                  className="input-base text-sm"
                >
                  <option value="all">Svi gradovi</option>
                  {cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-3 text-gray-900 dark:text-gray-100">
                  Cena (RSD/h)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="number"
                    value={filters.minPrice}
                    onChange={(e) => updateFilters({...filters, minPrice: e.target.value})}
                    placeholder="Min"
                    className="input-base text-sm"
                  />
                  <input
                    type="number"
                    value={filters.maxPrice}
                    onChange={(e) => updateFilters({...filters, maxPrice: e.target.value})}
                    placeholder="Max"
                    className="input-base text-sm"
                  />
                </div>
              </div>
            </>
          )}

            <div className="sticky bottom-0 bg-white dark:bg-gray-800 pt-4 pb-6 border-t border-gray-200 dark:border-gray-700 mt-6">
              <button
                onClick={() => setShowMobileFilters(false)}
                className="btn-primary w-full"
              >
                Prikaži {filteredResults.length} rezultata
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    // Desktop Filter Panel - Same content structure
    return (
      <div className="card bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Filteri</h3>
          {activeCount > 0 && (
            <button
              onClick={clearAllFilters}
              className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
            >
              Resetuj sve
            </button>
          )}
        </div>

        {viewMode === 'offers' ? (
          <>
            {/* Price Range */}
            <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium mb-3 text-gray-900 dark:text-gray-100">
                Cena po satu (RSD)
              </label>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  value={filters.minPrice}
                  onChange={(e) => updateFilters({...filters, minPrice: e.target.value})}
                  placeholder="Min"
                  className="input-base text-sm"
                />
                <input
                  type="number"
                  value={filters.maxPrice}
                  onChange={(e) => updateFilters({...filters, maxPrice: e.target.value})}
                  placeholder="Max"
                  className="input-base text-sm"
                />
              </div>
            </div>

            {/* Experience */}
            <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium mb-3 text-gray-900 dark:text-gray-100">
                Iskustvo
              </label>
              <div className="space-y-2">
                {['0-1', '2-5', '5+'].map(exp => (
                  <label key={exp} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="experience"
                      checked={filters.experience === exp}
                      onChange={() => updateFilters({...filters, experience: exp})}
                      className="w-4 h-4 text-primary-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {exp === '0-1' ? '0-1 godina' : exp === '2-5' ? '2-5 godina' : '5+ godina'}
                    </span>
                  </label>
                ))}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="experience"
                    checked={filters.experience === 'all'}
                    onChange={() => updateFilters({...filters, experience: 'all'})}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Sve</span>
                </label>
              </div>
            </div>

            {/* Availability */}
            <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium mb-3 text-gray-900 dark:text-gray-100">
                Dostupnost
              </label>
              <div className="space-y-2">
                {['weekdays', 'weekend', 'evenings'].map(avail => (
                  <label key={avail} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.availability.includes(avail)}
                      onChange={(e) => {
                        const newAvail = e.target.checked
                          ? [...filters.availability, avail]
                          : filters.availability.filter(a => a !== avail);
                        updateFilters({...filters, availability: newAvail});
                      }}
                      className="w-4 h-4 text-primary-600 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {avail === 'weekdays' ? 'Radni dani' : avail === 'weekend' ? 'Vikend' : 'Večeri'}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Rating */}
            <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium mb-3 text-gray-900 dark:text-gray-100">
                Ocena
              </label>
              <div className="space-y-2">
                {['4', '3'].map(rating => (
                  <label key={rating} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="rating"
                      checked={filters.minRating === rating}
                      onChange={() => updateFilters({...filters, minRating: rating})}
                      className="w-4 h-4 text-primary-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {rating}+ ⭐
                    </span>
                  </label>
                ))}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="rating"
                    checked={!filters.minRating}
                    onChange={() => updateFilters({...filters, minRating: ''})}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Sve</span>
                </label>
              </div>
            </div>

            {/* City */}
            <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium mb-3 text-gray-900 dark:text-gray-100">
                Grad
              </label>
              <select
                value={filters.city}
                onChange={(e) => updateFilters({...filters, city: e.target.value})}
                className="input-base text-sm"
              >
                <option value="all">Svi gradovi</option>
                {cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>

            {/* Distance Filter */}
            {selectedLocation && (
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-medium mb-3 text-gray-900 dark:text-gray-100">
                  Udaljenost
                </label>
                <select
                  value={filters.distance}
                  onChange={(e) => updateFilters({...filters, distance: e.target.value})}
                  className="input-base text-sm"
                >
                  <option value="all">Sve udaljenosti</option>
                  <option value="5">Do 5 km</option>
                  <option value="10">Do 10 km</option>
                  <option value="20">Do 20 km</option>
                </select>
              </div>
            )}

            {/* Verified */}
            <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.verified}
                  onChange={(e) => updateFilters({...filters, verified: e.target.checked})}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Verifikovan profil
                </span>
              </label>
            </div>

            {/* Non-smoker */}
            <div className="mb-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.nonSmoker}
                  onChange={(e) => updateFilters({...filters, nonSmoker: e.target.checked})}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Ne puši
                </span>
              </label>
            </div>
          </>
        ) : (
          <>
            {/* Job filters */}
            <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium mb-3 text-gray-900 dark:text-gray-100">
                Grad
              </label>
              <select
                value={filters.city}
                onChange={(e) => updateFilters({...filters, city: e.target.value})}
                className="input-base text-sm"
              >
                <option value="all">Svi gradovi</option>
                {cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-3 text-gray-900 dark:text-gray-100">
                Cena (RSD/h)
              </label>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  value={filters.minPrice}
                  onChange={(e) => updateFilters({...filters, minPrice: e.target.value})}
                  placeholder="Min"
                  className="input-base text-sm"
                />
                <input
                  type="number"
                  value={filters.maxPrice}
                  onChange={(e) => updateFilters({...filters, maxPrice: e.target.value})}
                  placeholder="Max"
                  className="input-base text-sm"
                />
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // Active Filter Chips
  const ActiveFilterChips = () => {
    const chips = [];
    
    if (filters.city !== 'all') {
      chips.push({ key: 'city', label: filters.city, value: filters.city });
    }
    if (filters.minPrice) {
      chips.push({ key: 'minPrice', label: `Min: ${filters.minPrice} RSD`, value: '' });
    }
    if (filters.maxPrice) {
      chips.push({ key: 'maxPrice', label: `Max: ${filters.maxPrice} RSD`, value: '' });
    }
    if (filters.minRating) {
      chips.push({ key: 'minRating', label: `${filters.minRating}+ ⭐`, value: '' });
    }
    if (filters.experience !== 'all') {
      chips.push({ key: 'experience', label: filters.experience === '0-1' ? '0-1 god.' : filters.experience === '2-5' ? '2-5 god.' : '5+ god.', value: 'all' });
    }
    filters.availability.forEach(avail => {
      chips.push({ key: 'availability', label: avail === 'weekdays' ? 'Radni dani' : avail === 'weekend' ? 'Vikend' : 'Večeri', value: avail });
    });
    if (filters.verified) {
      chips.push({ key: 'verified', label: 'Verifikovan', value: false });
    }
    if (filters.nonSmoker) {
      chips.push({ key: 'nonSmoker', label: 'Ne puši', value: false });
    }

    if (chips.length === 0) return null;

    return (
      <div className="flex flex-wrap gap-2 mb-4">
        {chips.map((chip, idx) => (
          <span
            key={idx}
            className="px-3 py-1.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm flex items-center gap-2"
          >
            {chip.label}
            <button
              onClick={() => removeFilter(chip.key, chip.value)}
              className="hover:text-primary-900 dark:hover:text-primary-100 ml-1"
              aria-label="Ukloni filter"
            >
              <MdClose className="text-base" />
            </button>
          </span>
        ))}
      </div>
    );
  };

  // Result Card Component
  const ResultCard = ({ item, isBabysitter, onHover, onLeave, onContact }) => {
    if (isBabysitter) {
      const name = `${item.firstName || ''} ${item.lastName || ''}`.trim();
      const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
      const avatarUrl = item.photoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=${getAvatarColor(name)}&color=fff&size=128&bold=true`;
      
      return (
        <div
          className="card hover:shadow-lg transition-all cursor-pointer group border-2 border-transparent hover:border-primary-200 dark:hover:border-primary-800"
          onClick={() => window.open(`/babysitter/${item.id}`, '_blank', 'noopener,noreferrer')}
          onMouseEnter={onHover}
          onMouseLeave={onLeave}
        >
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              {item.photoUrl ? (
                <img 
                  src={item.photoUrl} 
                  alt={name}
                  className="w-16 h-16 rounded-[18px] object-cover border-2 border-gray-200 dark:border-gray-700"
                />
              ) : (
                <div 
                  className="w-16 h-16 rounded-[18px] border-2 border-gray-200 dark:border-gray-700 flex items-center justify-center text-white font-semibold text-xl"
                  style={{ backgroundColor: `#${getAvatarColor(name)}` }}
                >
                  {initials}
                </div>
              )}
              {item.isVerified && (
                <div className="absolute -bottom-1 -right-1 bg-primary-600 text-white p-1 rounded-full border-2 border-white dark:border-gray-800">
                  <MdVerified className="text-xs" />
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 pr-6">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 break-words">
                  {name}
                </h3>
                {item.isSupersitter && (
                  <span className="px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-xs font-medium">
                    Supersitter
                  </span>
                )}
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-1">
                <MdLocationOn className="text-base" />
                {item.address?.city || 'Beograd'}
              </p>

              {/* Rating */}
              <div className="flex items-center gap-2 mb-3">
                {renderStars(item.rating?.average || 0)}
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {item.rating?.average?.toFixed(1) || '0.0'} ({item.rating?.count || 0})
                </span>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-3">
                {item.experience && (
                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs">
                    Iskustvo {item.experience}+
                  </span>
                )}
                {item.responseTime && item.responseTime < 30 && (
                  <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs">
                    Brz odgovor
                  </span>
                )}
              </div>
            </div>

            {/* Right Side */}
            <div className="flex flex-col items-end gap-3 flex-shrink-0 min-w-[150px]">
              <div className="text-right w-full">
                <div className="text-xl font-bold text-primary-600 dark:text-primary-400">
                  {item.hourlyRate?.toLocaleString() || '0'} RSD/h
                </div>
              </div>
              <div className="flex flex-col gap-2 w-full">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/babysitter/${item.id}`, '_blank', 'noopener,noreferrer');
                  }}
                  className="btn-primary text-sm px-4 py-2 whitespace-nowrap w-full"
                >
                  Pogledaj profil
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onContact) {
                      onContact();
                    }
                  }}
                  className="btn-secondary text-sm px-4 py-2 whitespace-nowrap w-full flex items-center justify-center gap-2"
                >
                  <MdMessage />
                  Pošalji poruku
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      // Job card
      return (
        <div
          className="card hover:shadow-lg transition-all cursor-pointer group border-2 border-transparent hover:border-primary-200 dark:hover:border-primary-800"
          onClick={() => navigate(`/dashboard/jobs/${item.id}`)}
          onMouseEnter={onHover}
          onMouseLeave={onLeave}
        >
          <div className="flex justify-between items-start mb-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex-1">
              {item.title || 'Posao'}
            </h3>
            <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-xs font-medium">
              {item.status === 'posted' ? 'Aktivan' : item.status}
            </span>
          </div>

          {item.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
              {item.description}
            </p>
          )}

          <div className="space-y-2 mb-4">
            {item.location?.city && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <MdLocationOn />
                {item.location.city}
              </div>
            )}
            {item.scheduledDate && (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {new Date(item.scheduledDate).toLocaleDateString('sr-RS')}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="text-lg font-bold text-primary-600 dark:text-primary-400">
              {item.hourlyRate?.toLocaleString() || '0'} RSD/h
            </div>
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onContact) {
                    onContact();
                  }
                }}
                className="btn-secondary text-sm px-4 py-2 flex items-center gap-2"
              >
                <MdMessage />
                Pošalji poruku
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/dashboard/jobs/${item.id}`);
                }}
                className="btn-primary text-sm px-4 py-2"
              >
                Detalji
              </button>
            </div>
          </div>
        </div>
      );
    }
  };

  // Shared marketplace (offers + requests) — same UX/components
  /** Get lat/lng for item — use city fallback when coords missing (align with MapView). */
  const getLatLng = useCallback((item) => {
    const obj = (viewMode === 'offers') ? item?.address : item?.location;
    let lat = obj?.lat != null ? parseFloat(obj.lat) : (item?.lat != null ? parseFloat(item.lat) : null);
    let lng =
      obj?.lng != null ? parseFloat(obj.lng)
        : (obj?.lon != null ? parseFloat(obj.lon)
          : (item?.lng != null ? parseFloat(item.lng)
            : (item?.lon != null ? parseFloat(item.lon) : null)));
    if ((lat == null || lng == null) && obj?.city) {
      const cityCoords = getCityCoordinates(obj.city);
      if (cityCoords) {
        lat = lat ?? cityCoords.lat;
        lng = lng ?? cityCoords.lng;
      }
    }
    return { lat, lng };
  }, [viewMode]);

  const mapResults = useMemo(() => {
    if (!mapBounds) return filteredResults;
    const { north, south, east, west } = mapBounds;
    const inBounds = filteredResults.filter((item) => {
      const { lat, lng } = getLatLng(item);
      if (lat == null || lng == null) return false;
      return lat >= south && lat <= north && lng >= west && lng <= east;
    });
    return inBounds.length > 0 ? inBounds : filteredResults;
  }, [filteredResults, mapBounds, getLatLng]);

  const listBase = useMemo(() => {
    const bounds = searchAsIMove ? mapBounds : appliedListBounds;
    let base = filteredResults;
    if (bounds) {
      const { north, south, east, west } = bounds;
      const inBounds = filteredResults.filter((item) => {
        const { lat, lng } = getLatLng(item);
        if (lat == null || lng == null) return false;
        return lat >= south && lat <= north && lng >= west && lng <= east;
      });
      // Ako bounds isključuje sve — prikaži sve (kao fallback, map centar može biti pogrešan)
      base = inBounds.length > 0 ? inBounds : filteredResults;
    }
    // Filtriranje po kliknutom pin-u (jedan ili klaster)
    if (selectedPinItemIds && selectedPinItemIds.length > 0) {
      const idSet = new Set(selectedPinItemIds.map((id) => String(id)));
      base = base.filter((item) => item && idSet.has(String(item.id)));
    }
    return base;
  }, [filteredResults, searchAsIMove, mapBounds, appliedListBounds, getLatLng, selectedPinItemIds]);

  const listResultsPaged = useMemo(() => (
    listBase.slice(0, listPage * PAGE_SIZE)
  ), [listBase, listPage, PAGE_SIZE]);

  const hasMore = listResultsPaged.length < listBase.length;

  const toolbarSort = useMemo(() => {
    if (sortBy === 'recommended') return 'default';
    if (sortBy === 'price-low') return 'price_asc';
    if (sortBy === 'price-high') return 'price_desc';
    if (sortBy === 'rating') return 'rating';
    return 'default';
  }, [sortBy]);

  const handleToolbarSortChange = (v) => {
    if (v === 'default') return setSortBy('recommended');
    if (v === 'price_asc') return setSortBy('price-low');
    if (v === 'price_desc') return setSortBy('price-high');
    if (v === 'rating') return setSortBy('rating');
    return setSortBy('recommended');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50/40 via-gray-50 to-pink-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1600px]">
        {/* Hero-like search section (like landing) */}
        <section className="pt-6 sm:pt-8 pb-6 sm:pb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100">
              {viewMode === 'offers' ? 'Pronađi dadilju' : 'Pronađi posao'}
            </h1>
            {isParent && viewMode === 'requests' && (
              <button
                type="button"
                onClick={() => navigate('/dashboard/jobs/create')}
                className="btn-primary flex items-center justify-center gap-2 shrink-0"
              >
                <MdAdd className="text-lg" />
                Kreiraj posao
              </button>
            )}
          </div>

          {/* Mobile: collapse toggle */}
          <button
            onClick={() => setShowSearchHeader(!showSearchHeader)}
            className="lg:hidden w-full flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm mb-4"
          >
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate pr-2">
              {locationInput || 'Unesite grad ili naselje…'}
            </span>
            {showSearchHeader ? <MdExpandLess className="text-xl text-gray-500" /> : <MdExpandMore className="text-xl text-gray-500" />}
          </button>

          <div className={`${showSearchHeader ? 'block' : 'hidden'} lg:block`}>
            <form onSubmit={handleSearch} className="space-y-4">
              {/* Mode toggle — pill like landing */}
              <div className="flex items-center justify-center sm:justify-start">
                <div className="inline-flex items-center rounded-full border border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-1 shadow-sm">
                  <button
                    type="button"
                    onClick={() => {
                      setViewMode('offers');
                      const params = new URLSearchParams(searchParams);
                      params.set('mode', 'offers');
                      setSearchParams(params, { replace: true });
                    }}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                      viewMode === 'offers' ? 'bg-primary-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    Tražim dadilju
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setViewMode('requests');
                      const params = new URLSearchParams(searchParams);
                      params.set('mode', 'requests');
                      setSearchParams(params, { replace: true });
                    }}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                      viewMode === 'requests' ? 'bg-primary-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                    }`}
                  >
                    Tražim posao
                  </button>
                </div>
              </div>

              {/* Search bar — pill style like landing */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-0 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden focus-within:ring-2 focus-within:ring-primary-500/50 focus-within:border-primary-500 max-w-3xl">
                <div className="flex-1 min-w-0 rounded-t-full sm:rounded-l-full sm:rounded-r-none">
                  <LocationAutocomplete
                    value={locationInput}
                    onChange={setLocationInput}
                    onSelect={handleLocationSelect}
                    placeholder="Unesite grad ili naselje…"
                  />
                </div>
                <button
                  type="submit"
                  className="btn-primary rounded-b-full sm:rounded-r-full sm:rounded-l-none px-6 sm:px-8 py-3.5 text-sm font-semibold whitespace-nowrap"
                >
                  Pretraži
                </button>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400">
                Prikazano <span className="font-semibold text-gray-900 dark:text-gray-100">{filteredResults.length}</span> rezultata
                {locationInput && ` za "${locationInput}"`}
              </p>
            </form>
          </div>
        </section>

        {/* Toolbar + filters + list | map (same structure as landing) */}
        <div className="relative pb-8">
          <ResultsToolbar
            count={listResultsPaged.length}
            mapCount={mapBounds ? mapResults.length : null}
            sortBy={toolbarSort}
            onSortChange={handleToolbarSortChange}
            onOpenFilters={() => {
              if (typeof window !== 'undefined' && window.matchMedia?.('(min-width: 1024px)')?.matches) {
                setShowFiltersPanel((v) => !v);
              } else {
                setShowMobileFilters(true);
              }
            }}
            filtersOpen={typeof window !== 'undefined' && window.matchMedia?.('(min-width: 1024px)')?.matches ? showFiltersPanel : showMobileFilters}
            viewMode={mapViewMode === 'map' ? 'map' : 'list'}
            onViewModeChange={(next) => setMapViewMode(next === 'map' ? 'map' : 'list')}
            stickyTopClass="top-[4.5rem]"
          />

          {showFiltersPanel && (
            <div className="hidden lg:block mt-4">
              <FilterPanel />
            </div>
          )}

          {/* Grid: list (left) | map (right) — same as landing */}
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-[minmax(380px,42%)_minmax(0,58%)] gap-4 min-h-0">
            <div
              className={`${mapViewMode === 'map' ? 'hidden lg:block' : 'block'} bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-4 lg:p-5 max-h-[calc(100vh-14rem)] min-h-[480px] overflow-y-auto min-w-0`}
            >
              {selectedPinItemIds && selectedPinItemIds.length > 0 && (
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Prikazano {listBase.length} od pin-a na mapi
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedPinItemIds(null)}
                    className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                  >
                    Prikaži sve
                  </button>
                </div>
              )}
              <ResultsList
                items={listResultsPaged}
                isParent={viewMode === 'offers'}
                getAvatarUrl={viewMode === 'offers' ? (item) => item.photoUrl || item.photo_url || item.profileImage : undefined}
                selectedItemId={selectedPinItemIds?.length === 1 ? selectedPinItemIds[0] : null}
                hoveredItemId={null}
                onSelectItem={(item) => {
                  if (viewMode === 'offers') navigate(`/babysitter/${item.id}`);
                  else navigate(`/dashboard/jobs/${item.id}`);
                }}
                onContactClick={viewMode === 'offers' ? (item) => navigate(`/dashboard/conversations?userId=${item.id}`) : undefined}
                onHoverItem={undefined}
                loading={loading}
                error={error}
                onRetry={loadData}
                hasMoreResults={hasMore}
                loadingMore={false}
                onLoadMore={() => setListPage((p) => p + 1)}
                scrollToHovered={false}
                emptyInBounds={listResultsPaged.length === 0 && filteredResults.length > 0}
                emptyVariant={listResultsPaged.length === 0 && filteredResults.length > 0 ? 'noResultsInArea' : 'default'}
                emptyVariantProps={{
                  isParent: viewMode === 'offers',
                  onClear: () => {
                    setSearchQuery('');
                    setLocationInput('');
                    setFilters((f) => ({ ...f, city: 'all', distance: 'all' }));
                    setSelectedLocation(null);
                  },
                }}
                listHeight={520}
              />
            </div>

            <div
              className={`${mapViewMode === 'list' ? 'hidden lg:block' : 'block'} rounded-2xl overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700 h-[400px] min-h-[360px] lg:h-[calc(100vh-14rem)] lg:min-h-[480px] lg:min-h-0 bg-gray-100 dark:bg-gray-800`}
            >
              <MapPanel
                key={selectedLocation?.lat != null ? `${selectedLocation.lat}-${selectedLocation.lon}` : 'no-location'}
                className="h-full min-h-0"
                showMapFooter={false}
                markerHoverTooltipOnly
                disableTooltips
                items={mapResults}
                isParent={viewMode === 'offers'}
                userAddress={selectedLocation?.lat != null && selectedLocation?.lon != null
                  ? { lat: selectedLocation.lat, lng: selectedLocation.lon, city: selectedLocation.city || selectedLocation.label?.split(',')[0], label: selectedLocation.label, nearby: true }
                  : (user?.address?.lat && user?.address?.lng
                    ? { lat: parseFloat(user.address.lat), lng: parseFloat(user.address.lng), city: user.address.city || 'Beograd', label: user.address.city || 'Beograd', nearby: true }
                    : { lat: 44.7866, lng: 20.4489, city: 'Beograd', label: 'Beograd', nearby: true })}
                friends={friends}
                selectedItemId={selectedPinItemIds?.length === 1 ? selectedPinItemIds[0] : null}
                hoveredItemId={null}
                onMarkerClick={(itemOrItems) => {
                  const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems];
                  const ids = items.map((i) => i.id).filter(Boolean);
                  if (ids.length > 0) {
                    setSelectedPinItemIds(ids);
                    setListPage(1);
                    setMapViewMode('list'); // mobile: pokaži filtriranu listu odmah posle klika na pin
                  }
                }}
                onMarkerHover={undefined}
                searchAsIMove={true}
                showSearchThisArea={false}
                onSearchThisArea={() => {
                  setAppliedListBounds(mapBounds);
                  setListPage(1);
                }}
                onMapMove={(bounds) => {
                  setMapBounds(bounds);
                  setShowSearchAreaButton(true);
                }}
                onRecenter={() => {}}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Filter Modal */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end lg:hidden">
          <FilterPanel isMobile={true} />
        </div>
      )}
      
      {/* Paywall Modal - z-50 */}
      <PaywallModal
        isOpen={showPaywallModal}
        onClose={() => setShowPaywallModal(false)}
        type="message"
      />
    </div>
  );
}
